import type { CSSProperties } from 'react';
import { formatMoney, roleForCents, MONEY_ROLE_VAR, type MoneyRole } from './moneyFormat';

export type { MoneyRole } from './moneyFormat';

/** Props for {@link MoneyText}. */
export interface MoneyTextProps {
  /** The amount in integer cents. May be negative. */
  cents: number;
  /**
   * Overrides the colour role. Defaults to `roleFor(cents)`: positive → `in`,
   * negative → `out`, zero → `flat`. Pass this when the figure's role is a
   * business fact rather than its sign — e.g. a P&L total that nets negative
   * for the *period* renders `down` (amber), not `out` (neutral ink), even
   * though both are numerically negative. See `docs/redesign-rulings.md` and
   * `mtb-design-system/tokens/vendor-app.css` for the rule this encodes:
   * **money out is not an error** — `out` is neutral ink and signed, never
   * red. Red is reserved for broken things.
   */
  role?: MoneyRole;
  /** Prefix positive amounts with `+`. Negative amounts always show the minus sign. Default `false`. */
  signed?: boolean;
  /** Font size in px (or any valid CSS size). Default `15`. */
  size?: number | string;
  /** Font weight. Default `600`. */
  weight?: number;
  /** `data-testid` for Playwright / RTL assertions on the exact figure text. */
  'data-testid'?: string;
}

/**
 * MoneyText — the money-figure text role: mono, tabular numerals,
 * decimal-aligned. Every rendered dollar figure that gets compared down a
 * column or a row should go through this component rather than a one-off
 * `<Text>`, so alignment and colour stay consistent across the app.
 *
 * Shell: any staff shell — (desktop) and the responsive management routes.
 * Never rendered in (kiosk): money figures are staff-only data (see
 * `docs/multi-seat-accounts.md` §7.0 and CLAUDE.md guardrail #2).
 *
 * Non-obvious contract: colour is a semantic ROLE, not a raw sign check.
 * `out` (money leaving, e.g. a buy or an expense) is neutral ink, not red —
 * "money out is not an error" is a deliberate design position (see
 * `mtb-design-system/templates/vendor-home/notes.md`). Red is reserved for
 * broken things and is never applied by this component. Owed/owing direction
 * must additionally be stated in words by the caller (see
 * `features/money/lib/positionWords.ts`) — colour alone never carries that
 * meaning, because colour-only encodings are inaccessible and because a
 * minus sign cannot say which way a debt points.
 *
 * Pure formatting logic (`formatMoney`, `roleForCents`) lives in
 * `./moneyFormat.ts`, not here — this module exports only the component so
 * React Fast Refresh can hot-reload it (`react-refresh/only-export-components`).
 */
export function MoneyText({
  cents,
  role,
  signed = false,
  size = 15,
  weight = 600,
  'data-testid': testId,
}: MoneyTextProps) {
  const resolvedRole = role ?? roleForCents(cents);
  const style: CSSProperties = {
    fontFamily: 'var(--mantine-font-family-monospace)',
    fontSize: size,
    fontWeight: weight,
    color: MONEY_ROLE_VAR[resolvedRole],
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
  };
  return (
    <span style={style} data-testid={testId}>
      {formatMoney(cents, { signed })}
    </span>
  );
}
